import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import LiveExchangeRate from "../../components/LiveExchangeRate";

const TravelInformation = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Health & Vaccinations",
      icon: "Heart",
      content: "While no mandatory vaccinations are required for Nepal, certain immunizations are strongly recommended for your safety.",
      items: ["Hepatitis A & B", "Typhoid", "Tetanus", "Rabies (for trekkers)", "Malaria prophylaxis (for Chitwan/Terai regions)", "COVID-19 vaccination recommended"]
    },
    {
      title: "Currency & Money",
      icon: "DollarSign",
      content: "The Nepali Rupee (NPR) is the local currency. ATMs are widely available in cities and popular trekking routes.",
      items: [<LiveExchangeRate key="rate" />, "ATMs available in all major cities", "Credit cards accepted in hotels and larger shops", "Carry cash for remote areas and small purchases", "Trekking areas: bring enough cash for the trip"]
    },
    {
      title: "Packing Essentials",
      icon: "Backpack",
      content: "What to pack depends on your season and activities, but these essentials apply year-round:",
      items: ["Layered clothing for varying temperatures", "Waterproof jacket and pants", "Comfortable trekking boots", "Sun protection: hat, sunglasses, sunscreen SPF 50+", "First aid kit and personal medications", "Water purification tablets or filter", "Power bank (frequent power outages)", "Travel insurance documents"]
    },
    {
      title: "Local Customs & Etiquette",
      icon: "Users",
      content: "Nepal is a culturally rich and conservative country. Respecting local customs goes a long way.",
      items: ["Remove shoes before entering temples and homes", "Dress modestly, especially at religious sites", "Ask permission before taking photos of people", "Use right hand for giving/receiving items", "Avoid public displays of affection", "Do not touch offerings or items in temples", "Never point feet at people or religious objects"]
    },
    {
      title: "Connectivity",
      icon: "Wifi",
      content: "Staying connected in Nepal is easy in cities, but limited in remote trekking areas.",
      items: ["Buy a local SIM at airport (Ncell or Nepal Telecom)", "WiFi available in most hotels and cafes in cities", "WiFi available in tea houses on popular trekking routes (may cost extra)", "Mobile data coverage limited above 3,500m", "E-SIM options available for international travelers"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&h=600&fit=crop)" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="section-heading text-white mb-4">Travel Information</h1>
              <p className="text-lg text-white/80">Essential tips and practical information for your Nepal journey.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            {sections.map((section, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Icon name={section.icon} size={18} className="text-accent" />
                  </div>
                  <h2 className="font-headline text-xl text-gray-900" style={{ fontWeight: 300 }}>{section.title}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">{section.content}</p>
                <ul className="space-y-2">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      {typeof item === "string" && <Icon name="CheckCircle" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />}
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-white/50">
          &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TravelInformation;
